import React, { useEffect, useState } from 'react';
import { Flex, Segmented, Button, Typography, Tag, Spin, message, Checkbox, Tooltip } from 'antd';
import { CheckCircleFilled, CloseCircleFilled, StarFilled, CreditCardOutlined } from '@ant-design/icons';
import {
  useGetSubscriptionStatusQuery,
  useCreateCheckoutMutation,
  useCreatePortalMutation,
  useLogConsentMutation,
} from '../../services/Subscription';
import './PlansAndBilling.css';

const { Title, Text } = Typography;

const PLANS = {
  monthly: {
    priceId: 'price_1TJAcARwpVgcrAJkk33JWjYS',
    price: 14.99,
    period: 'Monat',
    label: 'Monatlich',
    perMonth: 14.99,
  },
  quarterly: {
    priceId: 'price_1TJAdCRwpVgcrAJk8A0wMgfe',
    price: 38.97,
    period: 'Quartal',
    label: 'Quartalsweise',
    perMonth: 12.99,
    save: '13%',
  },
};

const FREE_FEATURES = [
  { text: 'Investment Score & Verdict', included: true },
  { text: 'Suchergebnis-Bewertung', included: true },
  { text: 'Bis zu 5 Objekte speichern', included: true },
  { text: 'Vollständige Exposé-Analyse', included: false },
];

// Step 6: Reduced to 4 focused items
const PRO_FEATURES = [
  { text: 'Vollständige Exposé-Analyse', included: true, bold: true },
  { text: 'Cashflow & Rendite', included: true, bold: true },
  { text: 'Marktvergleich', included: true, bold: true },
  { text: 'Unbegrenzt Objekte speichern', included: true, bold: true },
];

function FeatureItem({ text, included, bold }) {
  return (
    <div className="pnb-feature-item">
      {included ? (
        <CheckCircleFilled style={{ color: '#1DB3B3', fontSize: 16 }} />
      ) : (
        <CloseCircleFilled style={{ color: '#D0D5DD', fontSize: 16 }} />
      )}
      <span className={`pnb-feature-text ${!included ? 'disabled' : ''} ${bold ? 'bold' : ''}`}>
        {text}
      </span>
    </div>
  );
}

export default function PlansAndBillingComponent() {
  const { data: subscription, isLoading } = useGetSubscriptionStatusQuery();
  const [createCheckout, { isLoading: checkoutLoading }] = useCreateCheckoutMutation();
  const [createPortal, { isLoading: portalLoading }] = useCreatePortalMutation();
  const [logConsent] = useLogConsentMutation();
  const [billingInterval, setBillingInterval] = useState('monthly');

  // Step 1: 3 state variables consolidated into 1
  const [consentAll, setConsentAll] = useState(false);
  const allChecked = consentAll;

  const isPro = subscription?.plan === 'pro';
  const selectedPlan = PLANS[billingInterval];

  // Handle Stripe redirect params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      message.success('Upgrade erfolgreich! Dein Pro-Zugang ist jetzt aktiv.');
      window.history.replaceState({}, '', '/plans-and-billing');
    }
    if (params.get('canceled') === 'true') {
      message.info('Checkout abgebrochen.');
      window.history.replaceState({}, '', '/plans-and-billing');
    }
  }, []);

  const handleUpgrade = async () => {
    try {
      // Step 1: All 3 consent fields sent as hardcoded true for backend compatibility.
      // The single checkbox covers AGB + Widerruf legally; Datenschutz is shown as inline text.
      const consentResult = await logConsent({
        plan: billingInterval,
        agb: true,
        widerruf: true,
        datenschutz: true,
      });
      if (consentResult.error) {
        message.error('Fehler beim Speichern der Zustimmung. Bitte versuche es erneut.');
        return;
      }
      const { data } = await createCheckout({ priceId: selectedPlan.priceId });
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      message.error('Fehler beim Verarbeiten deiner Anfrage. Bitte versuche es erneut.');
    }
  };

  const handleManage = async () => {
    try {
      const { data } = await createPortal();
      if (data?.url) window.location.href = data.url;
    } catch {
      message.error('Fehler beim Öffnen des Kundenportals.');
    }
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 400 }}>
        <Spin size="large" />
      </Flex>
    );
  }

  return (
    <div className="pnb-container">
      {isPro ? (
        /* Pro-User behalten kleinen Header für Orientierung */
        <div className="pnb-header">
          <Title level={3} style={{ marginBottom: 4, color: '#1F1F1F' }}>Abo & Abrechnung</Title>
          <Text style={{ fontSize: 14, color: '#475467' }}>
            Verwalte dein Abonnement und deine Zahlungsinformationen.
          </Text>
        </div>
      ) : null}

      {isPro ? (
        /* ═══════════════════════════
           PRO USER VIEW — unverändert
        ═══════════════════════════ */
        <div className="pnb-pro-view">
          {/* Status card */}
          <div className="pnb-status-card">
            <Flex justify="space-between" align="center" style={{ marginBottom: 20 }}>
              <Flex align="center" gap={14}>
                <div className="pnb-status-icon">
                  <StarFilled style={{ fontSize: 20, color: '#1DB3B3' }} />
                </div>
                <div>
                  <div className="pnb-status-plan-name">ImmoSight Pro</div>
                  <div className="pnb-status-plan-label">
                    {subscription?.cancelAtPeriodEnd ? 'Wird gekündigt' : 'Aktives Abonnement'}
                  </div>
                </div>
              </Flex>
              <Tag
                color={subscription?.cancelAtPeriodEnd ? 'warning' : 'success'}
                style={{ fontSize: 12, fontWeight: 600, padding: '2px 12px', borderRadius: 8 }}
              >
                {subscription?.cancelAtPeriodEnd ? 'Wird gekündigt' : 'Aktiv'}
              </Tag>
            </Flex>

            <div className="pnb-status-details">
              <div className="pnb-status-detail">
                <div className="pnb-detail-label">Nächste Abrechnung</div>
                <div className="pnb-detail-value">
                  {subscription?.periodEnd
                    ? new Date(subscription.periodEnd).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })
                    : '—'}
                </div>
              </div>
              <div className="pnb-status-detail">
                <div className="pnb-detail-label">Status</div>
                <div className="pnb-detail-value" style={{ textTransform: 'capitalize' }}>
                  {subscription?.status === 'active' ? 'Aktiv' : subscription?.status || '—'}
                </div>
              </div>
            </div>

            <Flex gap={12} style={{ marginTop: 20 }}>
              <Button
                type="primary"
                onClick={handleManage}
                loading={portalLoading}
                style={{ background: '#1DB3B3', borderColor: '#1DB3B3', borderRadius: 8, height: 44, fontWeight: 600, fontSize: 14 }}
              >
                Abo verwalten
              </Button>
              <Button
                onClick={handleManage}
                loading={portalLoading}
                style={{ borderRadius: 8, height: 44, fontWeight: 600, fontSize: 14 }}
              >
                Zahlungsmethode ändern
              </Button>
            </Flex>
          </div>
        </div>
      ) : (
        /* ═══════════════════════════
           FREE USER VIEW
        ═══════════════════════════ */
        <div className="pnb-free-view">
          {/* Step 3: Conversion-Headline — direkt vor dem Billing-Toggle */}
          <h2 className="pnb-conversion-headline">
            Erkenne, ob sich dieses Objekt wirklich lohnt
          </h2>

          {/* Billing interval toggle */}
          <Flex justify="center" align="center" gap={12} className="pnb-billing-toggle">
            <Segmented
              value={billingInterval}
              onChange={setBillingInterval}
              options={[
                { label: 'Monatlich', value: 'monthly' },
                {
                  label: (
                    <Flex align="center" gap={6}>
                      Quartalsweise
                      <span className="pnb-save-badge">−13%</span>
                    </Flex>
                  ),
                  value: 'quarterly',
                },
              ]}
              style={{ padding: 4, borderRadius: 10 }}
              size="large"
            />
          </Flex>

          {/* Plan cards */}
          <div className="pnb-plans-grid">
            {/* Free card */}
            <div className="pnb-plan-card">
              <div className="pnb-plan-name">Free</div>
              <div className="pnb-plan-desc">Grundfunktionen zum Kennenlernen</div>

              <div className="pnb-plan-price-row">
                <span className="pnb-plan-price-amount">0 €</span>
              </div>
              <div className="pnb-plan-price-period">Für immer kostenlos</div>

              <Button
                disabled
                block
                style={{ height: 40, borderRadius: 8, fontWeight: 600, fontSize: 14, marginBottom: 20 }}
              >
                Aktueller Plan
              </Button>

              <div className="pnb-plan-divider" />
              <div className="pnb-features-title">Enthalten:</div>
              <div className="pnb-feature-list">
                {FREE_FEATURES.map((f, i) => (
                  <FeatureItem key={i} {...f} />
                ))}
              </div>
            </div>

            {/* Pro card */}
            <div className="pnb-plan-card recommended">
              <div className="pnb-plan-badge">Empfohlen</div>
              <div className="pnb-plan-name">
                <StarFilled style={{ color: '#1DB3B3', fontSize: 18, marginRight: 6 }} />
                Pro
              </div>
              <div className="pnb-plan-desc">Alles was du für fundierte Entscheidungen brauchst</div>

              <div className="pnb-plan-price-row">
                <span className="pnb-plan-price-amount">
                  {selectedPlan.perMonth.toFixed(2).replace('.', ',')} €
                </span>
                <span className="pnb-plan-price-suffix">/ Monat</span>
              </div>
              <div className="pnb-plan-price-period">
                {selectedPlan.price.toFixed(2).replace('.', ',')} € {billingInterval === 'monthly' ? '' : `pro ${selectedPlan.period}`}
                {selectedPlan.save && (
                  <span className="pnb-plan-save"> — {selectedPlan.save} sparen</span>
                )}
              </div>

              <div className="pnb-plan-divider" style={{ margin: '20px 0 16px 0' }} />

              {/* Step 6: Features-Titel geändert */}
              <div className="pnb-features-title">Was du bekommst:</div>
              <div className="pnb-feature-list" style={{ marginBottom: 20 }}>
                {PRO_FEATURES.map((f, i) => (
                  <FeatureItem key={i} {...f} />
                ))}
              </div>

              <div className="pnb-plan-divider" style={{ margin: '0 0 16px 0' }} />

              {/* Step 2: Legal-Block mit 1 Checkbox + Inline-Datenschutz */}
              <div className="pnb-legal-block">
                <Checkbox
                  checked={consentAll}
                  onChange={(e) => setConsentAll(e.target.checked)}
                  className="pnb-consent-item"
                >
                  <span className="pnb-consent-label">
                    Ich stimme den{' '}
                    <a
                      href="https://immosight.app/terms-conditions.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      AGB
                    </a>{' '}
                    zu und bestätige, dass der Zugang sofort freigeschaltet wird und mein Widerrufsrecht erlischt.
                  </span>
                </Checkbox>
              </div>

              {/* Step 4: Button mit Tooltip-Wrapper */}
              <Tooltip
                title={!allChecked ? 'Bitte bestätige die Bedingungen, um fortzufahren' : ''}
                placement="top"
              >
                <span style={{ display: 'block', cursor: !allChecked ? 'not-allowed' : 'default' }}>
                  <Button
                    type="primary"
                    block
                    disabled={!allChecked}
                    onClick={handleUpgrade}
                    loading={checkoutLoading}
                    style={{
                      height: 52,
                      borderRadius: 8,
                      fontWeight: 700,
                      fontSize: 16,
                      letterSpacing: '0.01em',
                      background: allChecked ? '#1DB3B3' : undefined,
                      borderColor: allChecked ? '#1DB3B3' : undefined,
                      pointerEvents: !allChecked ? 'none' : 'auto',
                    }}
                  >
                    Jetzt kostenpflichtig freischalten
                  </Button>
                </span>
              </Tooltip>

              {/* Step 5: Trust-Elemente direkt unter dem CTA */}
              <div className="pnb-trust-inline">
                <span className="pnb-trust-item">
                  <CheckCircleFilled style={{ color: '#1DB3B3', fontSize: 12 }} />
                  Sofort Zugriff
                </span>
                <span className="pnb-trust-separator">·</span>
                <span className="pnb-trust-item">
                  <CheckCircleFilled style={{ color: '#1DB3B3', fontSize: 12 }} />
                  Jederzeit kündbar
                </span>
                <span className="pnb-trust-separator">·</span>
                <span className="pnb-trust-item">
                  <CheckCircleFilled style={{ color: '#1DB3B3', fontSize: 12 }} />
                  Keine Mindestlaufzeit
                </span>
              </div>

              {/* Datenschutz-Inline-Text */}
              <div className="pnb-datenschutz-text">
                Mit dem Kauf akzeptierst du unsere{' '}
                <a
                  href="https://immosight.app/privacy-policy.html"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Datenschutzerklärung
                </a>
                .
              </div>
            </div>
          </div>

          {/* Globale Trust-Zeile — bleibt unverändert */}
          <Flex justify="center" style={{ marginTop: 24 }}>
            <Text style={{ fontSize: 12, color: '#98A2B3' }}>
              <CreditCardOutlined style={{ marginRight: 6 }} />
              Sichere Zahlung über Stripe · Jederzeit kündbar · Keine versteckten Kosten
            </Text>
          </Flex>
        </div>
      )}
    </div>
  );
}
